import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import axiosInstance from '../../services/axiosConfig';
import { ConversationThread } from './ConversationThread';

interface Email {
  id: string;
  front_id: string;
  subject: string;
  body: string;
  blurb: string;
  sender_email: string;
  sender_name: string;
  created_at: string;
  conversation_id: string;
  conversation_subject: string | null;
  is_inbound: boolean;
  attachments_count: number;
}

interface MatterMatch {
  matter_id: string;
  matter_name: string;
  matched_on: string;
  matched_value: string;
  entity_type: string;
}

interface AgentResult {
  matched: boolean;
  matter: MatterMatch | null;
  search_terms: string[];
  execution_time_ms: number;
  total_time_ms: number;
  trace: {
    email_subject: string;
    email_body_preview: string;
    sender_email: string;
    conversation_id: string;
    conversation_subject: string | null;
    attachments: Array<{
      filename: string;
      content_type: string;
      size: number;
      text_extracted: boolean;
    }>;
    firm_id: string;
  };
}

export const ZaddyEmailMatching = () => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [agentResult, setAgentResult] = useState<AgentResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [runningAgent, setRunningAgent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch emails on mount
  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        '/api/zaddy/email-matching/emails/',
        {
          params: { limit: 20 },
        },
      );
      setEmails(response.data.emails);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch emails');
    } finally {
      setLoading(false);
    }
  };

  const runAgent = async (email: Email) => {
    setSelectedEmail(email);
    setRunningAgent(true);
    setError(null);
    setAgentResult(null);

    try {
      const response = await axiosInstance.post(
        '/api/zaddy/email-matching/run-agent/',
        {
          message_id: email.id,
          conversation_id: email.conversation_id,
        },
      );
      // Show toast with workflow info - workflow runs in background
      toast.success(
        `Workflow started: ${response.data.workflow_id}. Check Temporal UI for progress.`,
        { duration: 5000 },
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run agent');
      toast.error(err.response?.data?.error || 'Failed to start workflow');
    } finally {
      setRunningAgent(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left Panel - Email List */}
      <Box
        sx={{
          width: '400px',
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" gutterBottom>
            Email Matching Sandbox
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test AI agent matching on production Front emails
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchEmails}
            sx={{ mt: 1 }}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh Emails'}
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {error && !agentResult && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {emails.length === 0 && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No emails found</Typography>
            </Box>
          )}

          {emails.map((email) => (
            <Card
              key={email.id}
              sx={{
                m: 1,
                cursor: 'pointer',
                bgcolor:
                  selectedEmail?.id === email.id
                    ? 'action.selected'
                    : 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => setSelectedEmail(email)}
            >
              <CardContent>
                <Typography variant="subtitle2" noWrap>
                  {email.subject || '(No subject)'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  From: {email.sender_name || email.sender_email}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, height: '40px', overflow: 'hidden' }}
                >
                  {email.blurb}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1 }}
                  alignItems="center"
                >
                  {email.attachments_count > 0 && (
                    <Chip
                      label={`${email.attachments_count} attachment(s)`}
                      size="small"
                    />
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      runAgent(email);
                    }}
                    disabled={runningAgent}
                  >
                    🤖 Run Agent
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Right Panel - Execution Trace */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {!selectedEmail && !agentResult && (
          <Box sx={{ textAlign: 'center', mt: 10 }}>
            <Typography variant="h6" color="text.secondary">
              Select an email and click &quot;Run Agent&quot; to see the
              matching flow
            </Typography>
          </Box>
        )}

        {selectedEmail &&
          !agentResult &&
          !runningAgent &&
          selectedEmail.conversation_id && (
            <ConversationThread
              conversationId={selectedEmail.conversation_id}
            />
          )}

        {runningAgent && (
          <Box sx={{ textAlign: 'center', mt: 10 }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Running AI Agent...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Extracting keywords and searching for matter
            </Typography>
          </Box>
        )}

        {agentResult && (
          <Stack spacing={3}>
            {/* Email Details Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                📧 Email Details
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Subject:
                  </Typography>
                  <Typography variant="body1">
                    {agentResult.trace.email_subject || '(No subject)'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    From:
                  </Typography>
                  <Typography variant="body1">
                    {agentResult.trace.sender_email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Body Preview:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      maxHeight: '200px',
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {agentResult.trace.email_body_preview}
                  </Typography>
                </Box>
                {agentResult.trace.attachments.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Attachments:
                    </Typography>
                    {agentResult.trace.attachments.map((att, idx) => (
                      <Chip
                        key={idx}
                        label={`${att.filename} (${(att.size / 1024).toFixed(1)} KB)`}
                        size="small"
                        sx={{ mr: 1, mt: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* AI Processing Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                🤖 AI Processing
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Search Terms Extracted:
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1, flexWrap: 'wrap' }}
                  >
                    {agentResult.search_terms.map((term, idx) => (
                      <Chip
                        key={idx}
                        label={term}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Execution Time:
                  </Typography>
                  <Typography variant="body1">
                    {agentResult.execution_time_ms.toFixed(1)} ms
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Time:
                  </Typography>
                  <Typography variant="body1">
                    {agentResult.total_time_ms.toFixed(1)} ms
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Match Result Section */}
            {agentResult.matched && agentResult.matter ? (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: 'success.light',
                  borderLeft: 4,
                  borderColor: 'success.main',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  ✅ Match Found
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2">Matter:</Typography>
                    <Typography variant="h6">
                      {agentResult.matter.matter_name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Matter ID:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {agentResult.matter.matter_id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Matched On:
                    </Typography>
                    <Chip
                      label={agentResult.matter.matched_on}
                      color="success"
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Matched Value:
                    </Typography>
                    <Typography variant="body1">
                      {agentResult.matter.matched_value}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Entity Type:
                    </Typography>
                    <Chip
                      label={agentResult.matter.entity_type}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Button
                    variant="contained"
                    href={`/matters/${agentResult.matter.matter_id}`}
                    target="_blank"
                    sx={{ mt: 2 }}
                  >
                    View Matter →
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: 'warning.light',
                  borderLeft: 4,
                  borderColor: 'warning.main',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  ❌ No Match Found
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1">
                  The AI agent could not find a matching matter for this email.
                </Typography>
                {agentResult.search_terms.length > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Searched for: {agentResult.search_terms.join(', ')}
                  </Typography>
                )}
              </Paper>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
